/**
 * @typedef {import('../types/faceLiveness').CreateFaceLivenessSessionResponse} CreateFaceLivenessSessionResponse
 */

import { getConfig } from "../config/index";
import { httpRequest } from "../utils/httpClient";
import { ApacuanaAPIError } from "../errors";
import ApacuanaSuccess from "../success";
import { INTEGRATION_TYPE } from "../utils/constant";

/**
 * Creates a new Face Liveness session.
 * @returns {Promise<CreateFaceLivenessSessionResponse>} Object with the session ID and a success indicator.
 * @throws {ApacuanaAPIError} If the API response is invalid or the integration type is not supported.
 * @throws {Error} If the request fails for another reason.
 */
const createFaceLivenessSessionOnBoarding = async () => {
  try {
    const response = await httpRequest(
      "services/api/faceliveness/create",
      {},
      "POST"
    );
    if (!response.sessionid) {
      throw new ApacuanaAPIError(
        "The API response does not contain the session ID.",
        response.status,
        "INVALID_API_RESPONSE"
      );
    }

    return new ApacuanaSuccess({
      sessionId: response.sessionid,
    });
  } catch (error) {
    if (error instanceof ApacuanaAPIError) {
      throw error;
    }
    throw new ApacuanaAPIError(
      `Failed to create Face Liveness session: ${error.message}`
    );
  }
};

const createFaceLivenessSessionOnPremise = async () => {
  throw new ApacuanaAPIError(
    "Creating a Face Liveness session is not supported for integration type: ONPREMISE",
    501,
    "NOT_IMPLEMENTED"
  );
};

const validateFaceLivenessOnBoarding = async (sessionId) => {
  try {
    const response = await httpRequest(
      "services/api/faceliveness/validate",
      { sessionid: sessionId },
      "POST"
    );
    return new ApacuanaSuccess({ status: "verified", ...response });
  } catch (error) {
    if (error instanceof ApacuanaAPIError) {
      let status;
      switch (error.statusCode) {
        case 402:
          status = "waitingForScan";
          break;
        case 403:
          status = "rejected";
          break;
        case 406:
          status = "processing";
          break;
        case 408:
          status = "expired";
          break;
        default:
          throw error;
      }
      return new ApacuanaSuccess({ status });
    }
    throw error;
  }
};

const validateFaceLivenessOnPremise = async () => {
  throw new ApacuanaAPIError(
    "Validating a Face Liveness session is not supported for integration type: ONPREMISE",
    501,
    "NOT_IMPLEMENTED"
  );
};

/**
 * Creates a new Face Liveness session.
 * @returns {Promise<CreateFaceLivenessSessionResponse>} Object with the session ID and a success indicator.
 * @throws {ApacuanaAPIError} If the API response is invalid or the integration type is not supported.
 * @throws {Error} If the request fails for another reason.
 */
export const createFaceLivenessSession = async () => {
  const { integrationType } = getConfig();

  if (integrationType === INTEGRATION_TYPE.ONBOARDING) {
    return createFaceLivenessSessionOnBoarding();
  }
  if (integrationType === INTEGRATION_TYPE.ONPREMISE) {
    return createFaceLivenessSessionOnPremise();
  }

  throw new ApacuanaAPIError(
    `Unsupported integration type: ${integrationType}`,
    400,
    "UNSUPPORTED_INTEGRATION_TYPE"
  );
};

/**
 * Validates a Face Liveness session and returns its status.
 * @param {{sessionId: string}} params - Object containing the session ID.
 * @returns {Promise<ApacuanaSuccess>} Object with the validation status.
 * @throws {ApacuanaAPIError} If the API response is invalid or the integration type is not supported.
 */
export const validateFaceLiveness = async ({ sessionId }) => {
  if (!sessionId) {
    throw new ApacuanaAPIError(
      "sessionId is a required parameter.",
      400,
      "INVALID_PARAMETER"
    );
  }

  const { integrationType } = getConfig();

  if (integrationType === INTEGRATION_TYPE.ONBOARDING) {
    return validateFaceLivenessOnBoarding(sessionId);
  }
  if (integrationType === INTEGRATION_TYPE.ONPREMISE) {
    return validateFaceLivenessOnPremise();
  }

  throw new ApacuanaAPIError(
    `Unsupported integration type: ${integrationType}`,
    400,
    "UNSUPPORTED_INTEGRATION_TYPE"
  );
};
